<template>
  <Head title="Admin — Bookings" />

  <div class="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black text-white">

    <!-- Animated background -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-fuchsia-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      <div class="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-cyan-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow-delayed"></div>
    </div>

    <!-- Nav -->
    <nav class="relative z-10 flex items-center justify-between px-4 md:px-10 py-5 border-b border-white/10 backdrop-blur-sm bg-black/20">
      <Link href="/" class="text-xl font-black tracking-tighter bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
        CLAUH.DJ
      </Link>
      <div class="flex items-center gap-3">
        <span class="hidden sm:block text-white/40 text-xs uppercase tracking-widest">Admin</span>
        <Link href="/" class="px-3 py-1.5 text-xs border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white">
          ← Site
        </Link>
        <a href="/session" data-turbo-method="delete" class="px-3 py-1.5 text-xs border border-white/10 rounded-lg hover:bg-white/5 transition-colors text-white/40 hover:text-white/70">
          Sair
        </a>
      </div>
    </nav>

    <div class="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">

      <!-- Page title -->
      <div class="mb-10">
        <h1 class="text-3xl md:text-4xl font-bold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
          Bookings
        </h1>
        <p class="text-white/40 text-sm mt-1">Gerencie as solicitações de apresentação</p>
      </div>

      <!-- Flash -->
      <div v-if="flash.notice" class="mb-8 p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm">
        {{ flash.notice }}
      </div>
      <div v-if="flash.alert" class="mb-8 p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
        {{ flash.alert }}
      </div>

      <!-- Stats row -->
      <div class="grid grid-cols-3 gap-3 md:gap-4 mb-10">
        <div class="p-4 rounded-xl bg-white/5 border border-yellow-500/20 text-center">
          <div class="text-2xl font-bold text-yellow-400">{{ pending_bookings.length }}</div>
          <div class="text-white/40 text-xs uppercase tracking-wider mt-1">Pendentes</div>
        </div>
        <div class="p-4 rounded-xl bg-white/5 border border-green-500/20 text-center">
          <div class="text-2xl font-bold text-green-400">{{ approved_bookings.length }}</div>
          <div class="text-white/40 text-xs uppercase tracking-wider mt-1">Aprovados</div>
        </div>
        <div class="p-4 rounded-xl bg-white/5 border border-red-500/20 text-center">
          <div class="text-2xl font-bold text-red-400">{{ rejected_bookings.length }}</div>
          <div class="text-white/40 text-xs uppercase tracking-wider mt-1">Rejeitados</div>
        </div>
      </div>

      <!-- Pending -->
      <section class="mb-10">
        <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-yellow-400 mb-4">
          <span class="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
          Pendentes
        </h2>

        <div v-if="pending_bookings.length === 0" class="p-8 rounded-xl bg-white/5 border border-white/10 text-white/30 text-center text-sm">
          Nenhuma solicitação pendente
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="booking in pending_bookings"
            :key="booking.id"
            class="rounded-xl bg-white/5 border border-yellow-500/20 hover:border-yellow-500/40 transition-all overflow-hidden"
          >
            <Link :href="`/admin/bookings/${booking.id}`" class="block p-5">
              <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div class="min-w-0">
                  <p class="font-semibold text-white truncate">{{ booking.name }}</p>
                  <p class="text-white/40 text-sm mt-0.5">{{ booking.event_location }}</p>
                </div>
                <div class="shrink-0 text-right">
                  <p class="text-fuchsia-400 font-medium text-sm">{{ formatDate(booking.event_date) }}</p>
                  <p class="text-white/30 text-xs mt-0.5">{{ formatDate(booking.created_at, true) }}</p>
                </div>
              </div>
              <p v-if="booking.message" class="mt-3 text-white/50 text-sm line-clamp-1">{{ booking.message }}</p>
            </Link>

            <div class="flex items-center gap-2 px-5 py-3 border-t border-white/5">
              <button
                @click="updateStatus(booking.id, 'approved')"
                class="px-4 py-1.5 bg-green-600/80 hover:bg-green-500 rounded-lg text-xs font-semibold transition-colors"
              >
                Aprovar
              </button>
              <button
                @click="updateStatus(booking.id, 'rejected')"
                class="px-4 py-1.5 bg-red-600/80 hover:bg-red-500 rounded-lg text-xs font-semibold transition-colors"
              >
                Rejeitar
              </button>
              <Link
                :href="`/admin/bookings/${booking.id}`"
                class="ml-auto text-xs text-white/30 hover:text-white/70 transition-colors"
              >
                Ver detalhes →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <!-- Approved -->
      <section class="mb-10">
        <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-green-400 mb-4">
          <span class="w-2 h-2 bg-green-400 rounded-full"></span>
          Próximos eventos
        </h2>

        <div v-if="approved_bookings.length === 0" class="p-8 rounded-xl bg-white/5 border border-white/10 text-white/30 text-center text-sm">
          Nenhum evento confirmado
        </div>

        <div v-else class="grid sm:grid-cols-2 gap-3">
          <Link
            v-for="booking in approved_bookings"
            :key="booking.id"
            :href="`/admin/bookings/${booking.id}`"
            class="block p-5 rounded-xl bg-white/5 border border-green-500/20 hover:border-green-500/40 transition-all"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="font-semibold text-white truncate">{{ booking.name }}</p>
                <p class="text-white/40 text-sm mt-0.5 truncate">{{ booking.event_location }}</p>
              </div>
              <span class="shrink-0 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium border border-green-500/30">
                confirmado
              </span>
            </div>
            <p class="text-green-400 font-medium text-sm mt-3">{{ formatDate(booking.event_date) }}</p>
          </Link>
        </div>
      </section>

      <!-- Rejected -->
      <section>
        <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white/30 mb-4">
          <span class="w-2 h-2 bg-red-400/60 rounded-full"></span>
          Rejeitados
        </h2>

        <div v-if="rejected_bookings.length === 0" class="p-8 rounded-xl bg-white/5 border border-white/10 text-white/30 text-center text-sm">
          Nenhuma rejeição
        </div>

        <div v-else class="space-y-2">
          <Link
            v-for="booking in rejected_bookings"
            :key="booking.id"
            :href="`/admin/bookings/${booking.id}`"
            class="flex items-center gap-4 px-5 py-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
          >
            <p class="flex-1 text-white/50 text-sm font-medium truncate">{{ booking.name }}</p>
            <p class="text-white/25 text-xs">{{ formatDate(booking.event_date) }}</p>
          </Link>
        </div>
      </section>

    </div>
  </div>
</template>

<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'

interface Booking {
  id: number
  name: string
  email: string
  phone: string | null
  event_location: string
  event_date: string | null
  message: string | null
  status: string
  created_at: string
}

defineProps<{
  pending_bookings: Booking[]
  approved_bookings: Booking[]
  rejected_bookings: Booking[]
  flash: { notice?: string; alert?: string }
}>()

function formatDate(dateStr: string | null, short = false): string {
  if (!dateStr) return 'Não informado'
  return new Date(dateStr).toLocaleDateString('pt-BR', short
    ? { day: '2-digit', month: '2-digit', year: 'numeric' }
    : { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
  )
}

function updateStatus(id: number, status: string) {
  router.patch(`/admin/bookings/${id}`, { booking: { status } })
}
</script>
